import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import GlitchText from './components/glitch';
import ScrollVelocity from './components/scrolltext';
import TiltedCard from './components/Photo';

type Experience = {
  role: string;
  company: string;
  period: string;
  summary: string;
  bullets?: string[];
  logo?: string;
  image?: string;
};

type Project = {
  name: string;
  stack: string[];
  description: string;
  meta: string;
  link?: string;
};

type Timeline = {
  title: string;
  org: string;
  period: string;
  detail: string;
  icon?: string;
};

type BlogPost = {
  id: string;
  title: string;
  tag: string;
  summary: string;
  date: string;
  content: string;
  image?: string;
};

const experiences: Experience[] = [
  {
    role: 'Software Engineer',
    company: 'Divine Research and Hospital',
    period: 'Mar 2026 – Present',
    summary: 'Building software systems and internal tools using Python in Odoo development.',
    bullets: [
      'Architecting and optimizing ERP solutions using Python and Odoo, automating hospital workflows and improving operational efficiency by 40%.',
      'Collaborating directly with the CEO to translate strategic requirements into scalable ERP modules, enhancing data centralization and reducing manual processes by 30%.',
    ],
    logo: '/1748861898753.jfif',
    image: '/1748861898753.jfif',
  },
  {
    role: 'Software Developer',
    company: 'Continental Forex',
    period: 'Oct 2025 – Feb 2026',
    summary: 'Full-stack HR ERP with performance optimization.',
    bullets: [
      'Developed a Full-Stack Employee Management ERP using Spring Boot, React.js, MySQL, improving HR workflow efficiency',
      'Engineered secure RESTful APIs with RBAC and scalable React dashboards, reducing manual operations andenhancing system performance by 20%.',
    ],
    logo: '/ddddd.png',
    image: '/ddddd.png',
  },
  {
    role: 'Full-Stack Developer',
    company: 'Vaishnav Technologies',
    period: 'Feb 2025 – May 2025',
    summary: 'Built and maintained RESTful backend services using Java and Spring Boot.',
    bullets: [
      'Built and maintained RESTful backend services using Java and Spring Boot, following clean architecture principles',
      'Executed 20+ API tests and debugging sessions using Postman, reducing bug turnaround time by 30%',
    ],
    logo: '/cloud_elevate_technologies_logo.jpg',
    image: '/cloud_elevate_technologies_logo.jpg',
  },
  {
    role: 'Software Developer',
    company: 'iNeuron.ai',
    period: 'Oct 2024 – Nov 2024',
    summary: 'Implemented backend logic for AI-driven applications.',
    bullets: [
      'Implemented backend logic in Python and Flask for AI-driven applications, reducing response latency by 18%',
      'Participated in 4 agile sprints, delivering modular code across 5 backend modules',
    ],
    logo: '/ineuron_ai_logo.jpg',
    image: '/ineuron_ai_logo.jpg',
  },
  {
    role: 'Technical Content Writer',
    company: 'CyberSasu',
    period: 'Jun 2024 – Jul 2024',
    summary: ' Published technical tutorials on cloud computing and DevOps.',
    bullets: [
      'Published 12+ technical tutorials on cloud computing, cybersecurity, and DevOps',
      'Increased organic search traffic by 40% through keyword optimization and SEO integration',
    ],
    logo: '/1714758504043.jpg',
    image: '/1714758504043.jpg',
  },
  {
    role: 'Technical Content Writer',
    company: 'BrandClickMedia',
    period: 'Oct 2023 – Oct 2023',
    summary: 'Authored articles on AI tools and ML workflows.',
    bullets: [
      'Authored 10+ articles on AI tools, ML workflows, and SaaS platforms for a technical audience',
      'Improved readability and SEO score by 25% through iterative editing and keyword analysis',
    ],
    logo: '/1630539323929.jfif',
    image: '/1630539323929.jfif',
  },
  {
    role: 'Content Writer',
    company: 'Aimstorms',
    period: 'May 2023 – Jul 2023',
    summary: 'Wrote advertising content aligned with brand messaging and marketing goals.',
    bullets: [
      'Write advertising content that aligned with brand messaging and marketing goals',
      'Developed SEO-optimized blogs, social media posts, and promotional material to improve online visibility',
      'Collaborated with graphic designers and digital marketers to ensure content consistency across platforms',
    ],
    logo: '/aimstorms_logo.jfif',
    image: '/aimstorms_logo.jfif',
  },
  {
    role: 'Content Writer',
    company: 'Shiksha.com',
    period: 'Apr 2022 – Sep 2022',
    summary: 'Developed high-quality educational content focused on college admissions and career guidance.',
    bullets: [
      'Developed high-quality educational content focused on college admissions, competitive exam preparation, and career guidance',
      'Conducted in-depth interviews and incorporated authentic student testimonials to enhance article relevance and engagement',
    ],
    logo: '/1631366558359 (1).jfif',
    image: '/1631366558359 (1).jfif',
  },
  {
    role: 'Technical Content Writer',
    company: 'GeeksforGeeks',
    period: 'Oct 2022 – Oct 2023',
    summary: 'Published 300+ tutorials on data structures and algorithms.',
    bullets: [
      'Published 300+ tutorials on data structures, algorithms, and core programming topics',
      'Updated legacy articles to align with new academic standards, improving engagement by 22%',
    ],
    logo: '/ddddd.png',
    image: '/ddddd.png',
  },
  {
    role: 'Subject Matter Expert (Computer Science)',
    company: 'Chegg',
    period: 'Mar 2023 – Sep 2023',
    summary: 'Solved technical questions across computer science and databases.',
    bullets: [
      'Maintained a 98% solution accuracy rate under strict daily deadlines',
      'Solved over 200 technical questions across computer science, databases, and software engineering',
    ],
    logo: 'chegg_inc__logo.jfif',
    image: 'chegg_inc__logo.jfif',
  },
];

const projects: Project[] = [
  {
    name: 'Forest Fire Detection Using CV',
    stack: ['Python', 'YOLO', 'OPEN CV', 'Streamlit'],
    description:
      'Deployed YOLO for real-time forest fire detection with <2s latency, reducing false positives by 15%.',
    meta: 'AI • Collaboration • Content',
    link: 'https://forest-fire-detection1.streamlit.app/',
  },
  {
    name: 'Snappy_ChatApp',
    stack: ['Flutter', 'React', 'Node.js', 'FastAPI', 'MongoDB'],
    description:
      'Cross-platform travel planner with Google Gemini AI integration. Features natural language generation, budget tracking, day-wise scheduling, influencer itineraries, and offline access.',
    meta: 'Mobile + Web • AI • Travel',
    link: 'https://github.com/aritrikg/Snappy_ChatApp',
  },
  {
    name: 'Movie-Website',
    stack: ['React', 'TypeScript', 'WebRTC', 'Node.js', 'WebSockets'],
    description:
      'Omegle-inspired platform for tech folks with STUN/TURN backed P2P video, randomized matching, and planned skill-based pairing for meaningful conversations.',
    meta: 'Real-time • P2P • Video',
    link: 'https://wondrous-gaufre-d5d649.netlify.app/',
  },
  {
    name: 'The-old-internet',
    stack: ['Flutter', 'Node.js', 'MongoDB', 'Cloudinary', 'JWT'],
    description:
      'Pet adoption with swipe UX, AI pet-care chatbot, 30+ service bookings, Duolingo-style training lessons, and curated 100+ product store.',
    meta: 'Mobile • Backend • Commerce',
    link: 'https://the-old-internet-ten.vercel.app',
  },
  {
    name: 'QuickBite--Food-Delivery-App',
    stack: ['HTML', 'CSS', 'JavaScript', 'Retro Design'],
    description:
      'QuickBite — a complete, responsive platform that makes online food ordering and management simple and efficient.',
    meta: 'Web • Design • Fun',
    link: 'https://github.com/aritrikg/QuickBite--Food-Delivery-App',
  },
  {
    name: "Barney-Stinson-s-Legendary-Desktop",
    stack: ['React', 'TypeScript', 'Figma'],
    description:
      'A creative website showing what Barney Stinson\'s laptop would look like. Went viral on Reddit with 15K+ visitors.',
    meta: 'Fun • Creative • Viral',
    link: 'https://barney-stinson-s-legendary-desktop.vercel.app/',
  },
  {
    name: 'Human-Mental-Health-Condition-Detection',
    stack: ['Python', 'Automation', 'Twitter API'],
    description:
      'Designed and trained a Convolutional Neural Network (CNN) model using the FER2013 dataset to identify emotional states such as happiness, sadness, anger, and fear.',
    meta: 'Automation • Social • Bot',
    link: 'https://human-mental-health-condition-detection.streamlit.app/?',
  },
  {
    name: 'Singitronic',
    stack: ['AR', 'Spark Studio', 'Camera'],
    description:
      'Singitronic is a modern eCommerce platform focused on selling a wide range of electronic products. The goal of this project was to build a complete web application.',
    meta: 'AR • Social • Viral',
    link: 'https://github.com/aritrikg/Singitronic/',
  },
];

const timelines: Timeline[] = [
  {
    title: 'Google Kick Start 2022',
    org: 'Rank 7669',
    period: '2022',
    detail: 'Built and pitched in a competitive hackathon setting.',
    icon: '🏆',
  },
  {
    title: 'SERE Hackathon',
    org: 'Rank 11',
    period: '2022',
    detail: 'Led campaigns, generated 250+ participants, maintained lead pipelines.',
    icon: '📢',
  },

  {
    title: 'Campus Ambassador',
    org: 'GeeksforGeeks',
    period: '2022-2023',
    detail: 'Food waste reduction and teaching underprivileged children.',
    icon: '📢',
  },
];

const blogSnippets: BlogPost[] = [
  {
    id: 'deep-linking-delight',
    title: 'Spring Security Integration with Spring Boot',
    tag: 'Spring Boot • Java',
    summary: 'Notes from shipping deep-linking on Flutter and React Native without breaking existing flows.',
    date: 'Jan 2025',
    image: '/deep-linking-delight.png',
    content: `Deep linking is one of those features that sounds simple until you ship it. I learned the hard way that sharing links to specific app states requires careful coordination between native platforms, routing layers, and data persistence.

The Problem: AnyFeast's deep-linking implementation started with a straightforward goal: let users share specific recipes and ingredients. But coordinating between Flutter's navigation stack, deep link parsing, and the backend's data availability proved trickier than expected.

Solution Architecture:
- URI Schemes: Set up proper deep link handling for both iOS and Android to intercept URLs
- Router Layer: Built a central routing controller to parse and validate deep links  
- Data Loading: Implemented optimistic loading to display while fetching from the backend
- Fallback Handling: Gracefully degrade when deep link targets don't exist

Key Learnings:
- Always test with the app in killed state (most common real-world scenario)
- Delay app initialization until deep link is fully resolved
- Use navigation stacks carefully—don't push too many routes on resume
- Plan for deprecated links and user-friendly error screens

The result: 50% better sharing rates and 15% higher engagement. Users could finally send friends specific recipes instead of vague links to the home feed.`,
  },
  {
    id: 'webrtc-lessons',
    title: 'WebRTC without tears: lessons from Orbit',
    tag: 'Real-time • WebRTC',
    summary: 'How I wired TURN/STUN, randomized matching, and kept streams stable under bad networks.',
    date: 'Dec 2024',
    image: '/webrtc-lessons.png',
    content: `Building Orbit—a random 1v1 video chat for tech enthusiasts—taught me that WebRTC is powerful but humbling. Managing peer connections, handling network degradation, and scaling a matching system revealed why most apps just use Twilio.

Setting Up Infrastructure:
WebRTC requires STUN and TURN servers to traverse NATs. I deployed:
- STUN: For NAT traversal (free tier sufficient)
  - TURN: For symmetric NAT (the expensive part, ~$20/GB)
- Fallback: Graceful degradation to audio-only if video stalls

Matching Engine:
Randomized matching sounds trivial until you consider:
- Connection availability (don't match while one peer is loading)
- Drop-off rates (people hate staring at loading spinners)
- Graceful cleanup (what happens when one peer quits mid-call?)

Solution: Queue-based matching with 30s timeouts and automatic reconnection attempts.

Keeping Streams Stable:
Under poor network conditions, video quality should degrade—not crash. I implemented:
- Adaptive bitrate management
- Fallback to audio when video bandwidth drops
- Reconnection with exponential backoff
- Clear UX indicators for network state

What I'd Do Differently:
- Use a managed service (Vonage, Twilio) for production
- Test extensively on 3G and weak WiFi
- Plan for regional TURN servers earlier
- Build analytics to understand drop-off points

WebRTC is worth the pain if peer-to-peer is truly required. For most video apps though, a managed platform saves months of debugging.`,
  },
  {
    id: 'terraform-automation',
    title: 'Terraform + SQS + Lambda for data automation',
    tag: 'Infra • AWS',
    summary: 'Reducing manual scraping by 40% with a small, reliable serverless pipeline.',
    date: 'Nov 2024',
    image: '/terraform-automation.png',
    content: `Saarthi's admin portal was bleeding time on manual data scraping. Every morning, someone would manually fetch data from three different sources, transform it, and load it into the database. It was error-prone and inefficient.

The Old Way:
- Manual scripts run on someone's laptop
- No retry logic (failed runs meant a day of missing data)
- No audit trail (hard to debug if data looked off)
- Scaling was impossible (adding new sources meant writing new scripts)

Infrastructure as Code:
I designed a serverless pipeline using Terraform to automate the entire process. The Lambda function triggers on schedule, picks up jobs from SQS, processes them with retries, and logs everything to CloudWatch for full visibility.

Key Features:
1. Automatic Retries: SQS + Lambda dead-letter queues catch failures
2. Idempotent Operations: Run the same job 10 times, same result
3. CloudWatch Logs: Full audit trail of every scrape
  4. Cost: ~$10/month (vs. $300 in developer time)

Results:
- 40% less manual effort
- 0 missed scrapes in 6 months
- Easy to add new data sources
- Clear visibility into pipeline health

The biggest win? No more 2am alerts about missing data. The entire pipeline just works, silently handling errors and retries in the background.`,
  },
  {
    id: 'flutter-state-management',
    title: 'State Management in Flutter: GetX vs Provider vs BLoC',
    tag: 'Flutter • Mobile',
    summary: 'A practical comparison from building production apps with all three approaches.',
    date: 'Oct 2024',
    image: '/flutter-state.png',
    content: `After building multiple Flutter apps with different state management solutions, I've developed strong opinions about when to use each one.

GetX: The Pragmatic Choice
GetX is controversial in the Flutter community, but I've found it incredibly productive for small-to-medium apps. The reactive state with .obs and Obx() widgets requires minimal boilerplate.

Pros:
- Minimal boilerplate, fast to implement
- Built-in dependency injection
- Route management included
- Great for rapid prototyping

Cons:
- Can become messy in large codebases
- Testing can be tricky
- Some consider it "too magical"

Provider: The Safe Choice
Provider is the official recommendation and works well for most apps. It's predictable and testable.

Pros:
- Official Flutter team recommendation
- Easy to test and debug
- Clear data flow
- Great documentation

Cons:
- More boilerplate than GetX
- Context dependency can be annoying
- Nested providers get complex

BLoC: The Enterprise Choice
BLoC shines in large, complex applications where separation of concerns matters most.

Pros:
- Clear separation of UI and business logic
- Highly testable
- Scales well for large teams
- Predictable state transitions

Cons:
- Significant boilerplate
- Steeper learning curve
- Overkill for simple apps

My Recommendation:
- Solo/small team + fast iteration → GetX
- Medium apps + need stability → Provider
- Large teams + complex business logic → BLoC`,
  },
  {
    id: 'react-native-performance',
    title: 'React Native Performance: 60 FPS or bust',
    tag: 'React Native • Performance',
    summary: 'Techniques I used to hit 60 FPS on the MyStar app with 500K+ users.',
    date: 'Sep 2024',
    image: '/rn-performance.png',
    content: `Working on MyStar at Trent taught me that React Native performance isn't just about avoiding re-renders—it's about understanding the bridge and native thread interactions.

Key Optimizations:

1. FlatList Optimization
- Use getItemLayout for fixed-height items
- Implement removeClippedSubviews
- Careful with keyExtractor (avoid array index)
- windowSize and maxToRenderPerBatch tuning

2. Image Loading
- Use FastImage instead of Image component
- Implement progressive loading for large images
- Cache aggressively with proper memory limits
- Resize images server-side when possible

3. Animation Performance
- Use useNativeDriver: true wherever possible
- Avoid animating layout properties (use transform instead)
- Reanimated 2 for complex gesture animations
- Profile with Flipper's performance monitor

4. Memory Management
- Clean up subscriptions and timers
- Use useMemo and useCallback appropriately
- Avoid inline object/array creation in render
- Monitor with Xcode Instruments / Android Profiler

5. Bundle Size
- Code splitting with React.lazy
- Audit dependencies regularly
- Use Hermes engine (major improvement)
- Enable RAM bundles for faster startup

Results:
- Consistent 60 FPS on scroll
- 40% faster app startup
- 25% smaller bundle size
- Happier users and better ratings`,
  },
  {
    id: 'building-ai-features',
    title: 'Adding AI to your app without breaking the bank',
    tag: 'AI • LLM',
    summary: 'How I integrated GPT and Gemini APIs while keeping costs under $50/month.',
    date: 'Aug 2024',
    image: '/ai-features.png',
    content: `AI features can easily blow up your cloud bill. Here's how I kept LikhAI's AI costs manageable while delivering real value to users.

Cost Control Strategies:

1. Smart Caching
- Cache common prompts and responses
- Use Redis for session-based caching
- Implement semantic similarity for cache hits
- Result: 60% reduction in API calls

2. Token Optimization
- Compress prompts without losing context
- Use system messages efficiently
- Truncate conversation history strategically
- Choose the right model for each task

3. Rate Limiting
- Per-user daily limits (generous but bounded)
- Queue system for non-urgent requests
- Graceful degradation during high load

4. Model Selection
- GPT-3.5-turbo for simple tasks (10x cheaper)
- GPT-4 only for complex reasoning
- Gemini Pro for content generation (good price/quality)
- Local models for privacy-sensitive tasks

5. Batching Requests
- Combine multiple small requests
- Background processing for non-real-time features
- Webhook-based async processing

Architecture Tips:
- Abstract the AI provider (easy to switch later)
- Build fallback chains (Gemini → GPT-3.5 → cached)
- Monitor usage per feature, not just total
- A/B test AI features for actual user value

Current Stack:
- Primary: Gemini Pro (best value)
- Fallback: GPT-3.5-turbo
- Premium features: GPT-4
- Total monthly cost: ~$45 for 10K+ active users`,
  },
  {
    id: 'mongodb-to-sql-migration',
    title: 'Migrating from MongoDB to SQL: A survival guide',
    tag: 'Database • Backend',
    summary: 'Lessons from a live migration at Trent with zero downtime.',
    date: 'Jul 2024',
    image: '/mongodb-sql.png',
    content: `When the MyStar team decided to migrate from MongoDB to SQL, I thought it would be a weekend project. It took three months.

Why Migrate?
- Complex reporting queries were slow
- Data consistency issues with embedded documents
- Team more familiar with SQL
- Better tooling for analytics

The Strategy:

1. Dual-Write Phase
- Write to both databases simultaneously
- Read from MongoDB (source of truth)
- Validate SQL data matches
- Duration: 4 weeks

2. Shadow Read Phase
- Still write to both
- Read from SQL, compare with MongoDB
- Log any discrepancies
- Duration: 2 weeks

3. SQL Primary Phase
- SQL becomes source of truth
- MongoDB as backup
- Monitor for issues
- Duration: 2 weeks

4. MongoDB Sunset
- Stop writes to MongoDB
- Keep for historical queries
- Eventually decommission

Schema Design Challenges:
- Embedded arrays → Junction tables
- Dynamic fields → JSON columns (PostgreSQL)
- References vs Embedding decisions
- Index strategy completely different

What Went Wrong:
- Underestimated data cleaning needs
- Some edge cases in date handling
- Connection pool sizing for SQL
- ORM learning curve for the team

What Went Right:
- Zero downtime achieved
- Query performance improved 10x for reports
- Data consistency issues resolved
- Team velocity improved long-term

Key Takeaway: Plan for 3x the time you think you need. Data migrations always have surprises.`,
  },
];

function BlogListPage({ blogSnippets, setCurrentPage }: { blogSnippets: BlogPost[], setCurrentPage: (page: string) => void }) {
  return (
    <div className="app-shell">
      <div className="blog-list-page">
        <div className="blog-list-header">
          <button onClick={() => setCurrentPage('home')} className="blog-back">← Back to home</button>
          <h1 className="blog-list-title">All Blog Posts</h1>
          <p className="blog-list-subtitle">Lessons learned while shipping</p>
        </div>
        <div className="cards-grid">
          {blogSnippets.map((post, idx) => (
            <article
              key={post.id}
              className="card card--link"
              style={{ animationDelay: `${idx * 80}ms` }}
              onClick={() => setCurrentPage(`blog-${post.id}`)}
              role="button"
              tabIndex={0}
            >
              <div className="card__row">
                <h3>{post.title}</h3>
              </div>
              <p className="summary">{post.summary}</p>
              <p className="blog-card__date">{post.date}</p>
              <span className="arrow">Read →</span>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}

function BlogPage({ postId, setCurrentPage }: { postId: string, setCurrentPage: (page: string) => void }) {
  const post = blogSnippets.find((p) => p.id === postId);
  if (!post) return <div>Post not found</div>;

  const paragraphs = post.content.split('\n\n');

  return (
    <div className="app-shell">
      <article className="blog-post">
        <div className="blog-post__header">
          <button onClick={() => setCurrentPage('blogs')} className="blog-back">← Back to blogs</button>
          <h1 className="blog-post__title">{post.title}</h1>
          <div className="blog-post__meta">
            <span className="pill pill--ghost">{post.tag}</span>
            <span className="blog-post__date">{post.date}</span>
          </div>
        </div>
        {post.image && (
          <img src={post.image} alt={post.title} className="blog-post__image" />
        )}
        <div className="blog-post__content">
          {paragraphs.map((para, i) => {
            if (para.startsWith('-')) {
              const lines = para.split('\n');
              return (
                <ul key={i} className="blog-list">
                  {lines.map((line, j) => (
                    <li key={j}>{line.replace(/^-\s*/, '').trim()}</li>
                  ))}
                </ul>
              );
            }
            return <p key={i}>{para}</p>;
          })}
        </div>
      </article>
    </div>
  );
}

const skills = {
  languages: ['Java', 'Python', 'C++', 'JavaScript', 'Kotlin'],
  technologies: [
    'React.js',
    'Node.js',
    'Spring Boot',
    'PostgreSQL',
    'REST APIs',
    'GraphQL',
    'Android',
  ],
  concepts: ['Git', 'Docker', 'Postman', 'CI/CD', 'Debugging', 'Agile', 'Firebase Console', 'AWS'],
};

function App() {
  const [expandedExp, setExpandedExp] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<'home' | string>('home');
  const sectionsRef = useRef<(HTMLElement | null)[]>([]);
  const [isMobile, setIsMobile] = useState<boolean>(() => typeof window !== 'undefined' && window.innerWidth < 900);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (currentPage !== 'home') return;

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    const sections = sectionsRef.current.filter(Boolean);
    sections.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => {
      sections.forEach((section) => {
        if (section) observer.unobserve(section);
      });
    };
  }, [currentPage]);

  // Handle blog navigation
  if (currentPage === 'blogs') {
    return <BlogListPage blogSnippets={blogSnippets} setCurrentPage={setCurrentPage} />;
  }
  
  if (currentPage !== 'home' && currentPage.startsWith('blog-')) {
    const postId = currentPage.replace('blog-', '');
    return (
      <BlogPage postId={postId} setCurrentPage={setCurrentPage} />
    );
  }

  return (
    <div className="app-shell">
      <header className="hero">
        <div className="hero__left">
          <div className="eyebrow">Software Engineer • Full Stack • ML</div>
          <GlitchText
            speed={0.8}
            enableShadows={true}
            enableOnHover={true}
            className="hero__title"
          >
            Aritrik Ghosh
          </GlitchText>
          <p className="hero__subtitle">
            Full-stack SDE specializing in backend systems and scalable APIs. I design and ship production services using Java, Python, Node.js, and React—from architecture to deployment.
          </p>
          <div className="hero__meta">
            <span>📍 Kolkata, India</span>
            <span>☎️ </span>
            <a href="mailto:aritrikghosh@proton.me">aritrikghosh@proton.me</a>
          </div>
          <div className="hero__links">
            <a href="https://github.com/aritrikg" className="chip" target="_blank" rel="noreferrer">GitHub</a>
            <a href="https://www.linkedin.com/in/aritrik-ghosh/" className="chip" target="_blank" rel="noreferrer">LinkedIn</a>
            <a href="https://leetcode.com/aritrikg/" className="chip" target="_blank" rel="noreferrer">LeetCode</a>
          </div>
        </div>
        <div className="hero__right">
          {isMobile ? (
            <TiltedCard
              imageSrc="/1631366558359 (1).jfif"
              altText="Aritrik Ghosh"
              containerHeight="300px"
              containerWidth="300px"
              imageHeight="300px"
              imageWidth="300px"
              rotateAmplitude={12}
              scaleOnHover={1.2}
              showMobileWarning={false}
              showTooltip={true}
            />
          ) : (
            <div className="hero__right-placeholder"></div>
          )}
        </div>
      </header>

      <section 
        id="experience" 
        className="section section--fade"
        ref={(el) => { sectionsRef.current[0] = el; }}
      >
        <div className="section__head">
          <p className="eyebrow">Work Experience</p>
          <h2>Shipping work that reaches users</h2>
        </div>
        <div className="exp-list">
          {experiences.map((exp, idx) => (
            <article
              key={exp.company}
              className={`exp-card ${expandedExp === exp.company ? 'exp-card--expanded' : ''}`}
              style={{ animationDelay: `${idx * 80}ms` }}
            >
              <button
                className="exp-card__header"
                onClick={() => setExpandedExp(expandedExp === exp.company ? null : exp.company)}
              >
                <img src={exp.logo} alt={exp.company} className="exp-card__logo" />
                <div className="exp-card__info">
                  <div className="exp-card__top">
                    <div>
                      <h3>{exp.role}</h3>
                      <p className="muted">{exp.company}</p>
                    </div>
                    <span className="pill pill--ghost">{exp.period}</span>
                  </div>
                  <p className="summary">{exp.summary}</p>
                </div>
                <span className="expand-icon">{expandedExp === exp.company ? '▼' : '▶'}</span>
              </button>

              {expandedExp === exp.company && (
                <div className="exp-card__details">
                  {exp.bullets && (
                    <ul className="bullet-list">
                      {exp.bullets.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </article>
          ))}
        </div>
      </section>

      <section 
        id="education" 
        className="section section--fade"
        ref={(el) => { sectionsRef.current[1] = el; }}
      >
        <div className="section__head">
          <p className="eyebrow">Education</p>
          <h2>Foundations</h2>
        </div>
        <div className="card">
          <div className="card__row">
            <img src="/sw.png" alt="Swami Vivekananda University" className="exp-card__logo" />
            <span className="pill">June 2025 – May 2027</span>
          </div>
          <h3>Swami Vivekananda University</h3>
          <p className="muted">M.Tech in Computer Science and Engineering </p>
        </div>
        <div className="card">
          <div className="card__row">
            <img src="/sw.png" alt="Swami Vivekananda University" className="exp-card__logo" />
            <span className="pill">Oct 2021 – May 2025</span>
          </div>
          <h3>Swami Vivekananda University</h3>
          <p className="muted">B.Tech in Computer Science and Engineering • GPA: 8.8</p>
        </div>
      </section>

      <section 
        id="skills" 
        className="section section--fade"
        ref={(el) => { sectionsRef.current[2] = el; }}
      >
        <div className="section__head">
          <p className="eyebrow">Skills</p>
          <h2>Toolkit at a glance</h2>
        </div>
        <div className="scroll-velocity-wrapper">
          <div className="scroll-velocity-section">
            <p className="scroll-velocity-label">Languages</p>
            <ScrollVelocity
              texts={[skills.languages.join(' • ')]}
              velocity={50}
              damping={50}
              stiffness={400}
              numCopies={6}
              className="scroll-velocity-text"
              parallaxClassName="scroll-velocity-parallax"
              scrollerClassName="scroll-velocity-scroller"
              velocityMapping={{ input: [0, 1000], output: [0, 3] }}
            />
          </div>
          <div className="scroll-velocity-section">
            <p className="scroll-velocity-label">Technologies</p>
            <ScrollVelocity
              texts={[skills.technologies.join(' • ')]}
              velocity={-50}
              damping={50}
              stiffness={400}
              numCopies={6}
              className="scroll-velocity-text"
              parallaxClassName="scroll-velocity-parallax"
              scrollerClassName="scroll-velocity-scroller"
              velocityMapping={{ input: [0, 1000], output: [0, 3] }}
            />
          </div>
                    

          <div className="scroll-velocity-section">
            <p className="scroll-velocity-label">Concepts</p>
            <ScrollVelocity
              texts={[skills.concepts.join(' • ')]}
              velocity={50}
              damping={50}
              stiffness={400}
              numCopies={6}
              className="scroll-velocity-text"
              parallaxClassName="scroll-velocity-parallax"
              scrollerClassName="scroll-velocity-scroller"
              velocityMapping={{ input: [0, 1000], output: [0, 3] }}
            />
          </div>
        </div>
      </section>

      <section 
        id="projects" 
        className="section section--fade"
        ref={(el) => { sectionsRef.current[3] = el; }}
      >
        <div className="section__head">
          <p className="eyebrow">Proof of Work</p>
          <GlitchText
            speed={1.2}
            enableShadows={true}
            enableOnHover={true}
            className="section__glitch-title"
          >
            Projects that reflect how I build
          </GlitchText>
        </div>
        <div className="cards-grid cards-grid--projects">
          {projects.map((project, idx) => (
            <a
              key={project.name}
              href={project.link || '#'}
              target={project.link ? '_blank' : '_self'}
              rel={project.link ? 'noreferrer' : ''}
              className={`card ${!project.link ? 'card--no-link' : ''}`}
              style={{ animationDelay: `${idx * 80}ms` }}
            >
              <div className="card__row">
                <h3>{project.name}</h3>
                {project.link && <span className="link-arrow">↗</span>}
              </div>
              <p className="summary">{project.description}</p>
              <div className="tag-row">
                {project.stack.map((tech) => (
                  <span key={tech} className="tag tag--ghost">{tech}</span>
                ))}
              </div>
            </a>
          ))}
        </div>
      </section>
      <section 
        id="timeline" 
        className="section section--fade"
        ref={(el) => { sectionsRef.current[4] = el; }}
      >
        <div className="section__head">
          <p className="eyebrow">Additional Experience</p>
          <h2>Hackathons & Leadership</h2>
        </div>
        <div className="timeline-compact">
          {timelines.map((item, idx) => (
            <div key={item.title} className="timeline-item" style={{ animationDelay: `${idx * 80}ms` }}>
              <span className="timeline-item__icon">{item.icon}</span>
              <div className="timeline-item__content">
                <h4>{item.title}</h4>
                <p className="muted">{item.org} • {item.period}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section
        id="coding-profiles"
        className="section section--fade"
        ref={(el) => { sectionsRef.current[5] = el; }}
      >
        <div className="section__head">
          <p className="eyebrow">Coding Profiles</p>
          <h2>Practice & Rankings</h2>
        </div>
        <div className="cards-grid cards-grid--profiles">
          <a className="card" href="https://codeforces.com/profile/NeuralDocCrafter" target="_blank" rel="noreferrer">
            <div className="card__row"><h3>Codeforces</h3><span className="link-arrow">↗</span></div>
            <p className="summary">Codeforces Expert Rated 1800+</p>
          </a>
          <a className="card" href="https://leetcode.com/aritrikg/" target="_blank" rel="noreferrer">
            <div className="card__row"><h3>LeetCode</h3><span className="link-arrow">↗</span></div>
            <p className="summary">Solved more than 1100 problems</p>
          </a>
          <a className="card" href="https://www.geeksforgeeks.org/profile/aritrikghosh784" target="_blank" rel="noreferrer">
            <div className="card__row"><h3>GeeksforGeeks</h3><span className="link-arrow">↗</span></div>
            <p className="summary">Coding Score 1000+</p>
          </a>
        </div>
      </section>

      <section 
        id="contact" 
        className="section contact section--fade"
        ref={(el) => { sectionsRef.current[6] = el; }}
      >
        <div>
          <p className="eyebrow">Contact</p>
          <h2>Let's build something users love</h2>
          <p className="summary">
            Open to roles where product reliability, performance, and shipping fast matter. Happy to share more work samples
            or dive into architecture.
          </p>
        </div>
        <div className="contact__actions">
          <a className="btn" href="mailto:aritrikghosh@proton.me">Email me</a>
          <a className="btn btn--ghost" href="https://github.com/aritrikg" target="_blank" rel="noreferrer">GitHub</a>
          <a className="btn btn--ghost" href="https://www.linkedin.com/in/aritrik-ghosh/" target="_blank" rel="noreferrer">LinkedIn</a>
        </div>
      </section>
    </div>
  );
}

export default App;
